import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Measure, AudioSettings, TimeSignature } from '../types';
import { parseChordToNotes, playNotes, playMetronomeClick, getAudioContext } from '../utils/audio';

// Development mode check - mirrors App.tsx
const IS_DEVELOPMENT = false;

export interface UseAudioPlaybackReturn {
  // State
  isPlaying: boolean;
  currentMeasure: number | null;
  currentChordIndex: number | null;
  startMeasureIndex: number;
  setStartMeasureIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentMeasure: React.Dispatch<React.SetStateAction<number | null>>;
  setCurrentChordIndex: React.Dispatch<React.SetStateAction<number | null>>;

  // Chart-level navigation context
  chartHasFine: boolean;
  chartHasToCoda: boolean;

  // Handlers
  handlePlayPause: () => void;
  handleRewind: () => void;
  handleStop: () => void;
  handlePlayChord: (chordString: string) => void;
  handleSetStartMeasure: (index: number) => void;
  clearAllTimers: () => void;
}

export function useAudioPlayback(
  displayedChart: Measure[],
  repeatPairMap: Record<number, number>,
  audioSettings: AudioSettings,
  timeSignature: TimeSignature,
  mode: string,
  isReadOnlyMode: boolean,
): UseAudioPlaybackReturn {
  // Audio & Transport State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMeasure, setCurrentMeasure] = useState<number | null>(null);
  const [currentChordIndex, setCurrentChordIndex] = useState<number | null>(null);
  const [startMeasureIndex, setStartMeasureIndex] = useState<number>(0);
  const mainTimerRef = useRef<number | null>(null);
  const chordTimersRef = useRef<number[]>([]);
  const jumpedFromMeasuresRef = useRef<Set<number>>(new Set());
  const repeatPassCountRef = useRef<Map<number, number>>(new Map());

  const clearAllTimers = useCallback(() => {
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }
    chordTimersRef.current.forEach(clearTimeout);
    chordTimersRef.current = [];
  }, []);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setCurrentMeasure(null);
    setCurrentChordIndex(null);
    jumpedFromMeasuresRef.current.clear();
    repeatPassCountRef.current.clear();
    clearAllTimers();
  }, [clearAllTimers]);

  // Enhanced navigation function that handles all musical annotations
  // Musical notation rules:
  // - Fine: Only stops playback AFTER a D.S. or D.C. has been executed
  // - To Coda: Only jumps to Coda AFTER a D.S. or D.C. has been executed
  // - First pass: Play straight through, ignore Fine and To Coda
  // - After D.S./D.C.: Fine stops, To Coda jumps
  // - Endings (1., 2., 3.): Only play on the corresponding repeat pass
  const getNextMeasureIndex = useCallback((currentMeasureIdx: number, playbackState: { 
    jumpedFromMeasuresRef: React.MutableRefObject<Set<number>>;
    repeatPassCount: React.MutableRefObject<Map<number, number>>;
    hasSeenDalSegno: boolean;
    hasSeenDalCapo: boolean;
    isInRepeatPass: boolean;
    segnoPosition?: number;
    codaPosition?: number;
  }): number => {
    const measure = displayedChart[currentMeasureIdx];
    const annotations = measure.annotations;
    
    if (!annotations) {
      return currentMeasureIdx + 1;
    }

    // Navigation priority: D.S./D.C. must be checked BEFORE Fine/To Coda
    // because Fine and To Coda only activate after a D.S./D.C. has set isInRepeatPass.
    // If D.S./D.C. and Fine were on the same measure, Fine must not block the jump.

    // Check for D.S. (Dal Segno) - go to Segno
    if (annotations.dalSegno && !playbackState.hasSeenDalSegno) {
      playbackState.hasSeenDalSegno = true;
      playbackState.isInRepeatPass = true;
      if (playbackState.segnoPosition !== undefined) {
        if (IS_DEVELOPMENT) console.log(`🎵 Playback: D.S. at measure ${currentMeasureIdx + 1} - jumping to Segno at measure ${playbackState.segnoPosition + 1}`);
        return playbackState.segnoPosition;
      }
      if (IS_DEVELOPMENT) console.log(`🎵 Playback: D.S. at measure ${currentMeasureIdx + 1} - no Segno found, jumping to beginning`);
      return 0;
    }

    // Check for D.C. (Dal Capo) - go to beginning
    if (annotations.dalCapo && !playbackState.hasSeenDalCapo) {
      playbackState.hasSeenDalCapo = true;
      playbackState.isInRepeatPass = true;
      if (IS_DEVELOPMENT) console.log(`🎵 Playback: D.C. at measure ${currentMeasureIdx + 1} - jumping to beginning`);
      return 0;
    }

    // Check for To Coda - jump to Coda section - ONLY after D.S. or D.C.
    if (annotations.toCoda && playbackState.codaPosition !== undefined && playbackState.isInRepeatPass) {
      if (IS_DEVELOPMENT) console.log(`🎵 Playback: To Coda at measure ${currentMeasureIdx + 1} - jumping to Coda at measure ${playbackState.codaPosition + 1}`);
      return playbackState.codaPosition;
    }

    // Check for Fine (end here) - ONLY after D.S. or D.C.
    if (annotations.fine && playbackState.isInRepeatPass) {
      if (IS_DEVELOPMENT) console.log(`🎵 Playback: Fine at measure ${currentMeasureIdx + 1} - ending playback (after D.S./D.C.)`);
      return displayedChart.length; // End playback
    }

    // Check for end repeat with endings logic
    const hasEndRepeat = annotations.endRepeat;
    if (hasEndRepeat && repeatPairMap[currentMeasureIdx] !== undefined) {
      const repeatStartIdx = repeatPairMap[currentMeasureIdx];
      const currentPass = playbackState.repeatPassCount.current.get(repeatStartIdx) || 1;
      
      let maxEndingNumber = 2;
      let hasAnyEndingMarker = false;
      
      if (IS_DEVELOPMENT) {
        console.log(`🎵 Finding maxEndingNumber for repeat at measure ${currentMeasureIdx + 1}, repeatStart=${repeatStartIdx + 1}`);
      }
      
      // Check within repeat section
      for (let i = repeatStartIdx; i <= currentMeasureIdx; i++) {
        const endingNum = displayedChart[i].annotations?.ending;
        if (IS_DEVELOPMENT && endingNum !== undefined) {
          console.log(`🎵 Measure ${i + 1} has ending: ${endingNum} (type: ${typeof endingNum})`);
        }
        if (endingNum) {
          hasAnyEndingMarker = true;
          if (endingNum > maxEndingNumber) maxEndingNumber = endingNum;
        }
      }
      
      // Check after repeat section for additional endings
      for (let i = currentMeasureIdx + 1; i < displayedChart.length; i++) {
        const endingNum = displayedChart[i].annotations?.ending;
        const hasStartRepeat = displayedChart[i].annotations?.startRepeat;
        const hasEndRepeat2 = displayedChart[i].annotations?.endRepeat;
        
        if (IS_DEVELOPMENT && endingNum !== undefined) {
          console.log(`🎵 After repeat - Measure ${i + 1} has ending: ${endingNum} (type: ${typeof endingNum}), startRepeat: ${hasStartRepeat}, endRepeat: ${hasEndRepeat2}`);
        }
        
        if (endingNum) {
          hasAnyEndingMarker = true;
          if (endingNum > maxEndingNumber) {
            maxEndingNumber = endingNum;
            if (IS_DEVELOPMENT) console.log(`🎵 Updated maxEndingNumber to ${maxEndingNumber}`);
          }
        }
        
        if (hasStartRepeat || hasEndRepeat2) break;
      }

      if (!hasAnyEndingMarker) maxEndingNumber = 2;
      
      if (IS_DEVELOPMENT) {
        console.log(`🎵 maxEndingNumber = ${maxEndingNumber}, hasAnyEndingMarker = ${hasAnyEndingMarker}`);
      }
      
      if (currentPass < maxEndingNumber) {
        playbackState.repeatPassCount.current.set(repeatStartIdx, currentPass + 1);
        if (IS_DEVELOPMENT) console.log(`🎵 Playback: End Repeat at measure ${currentMeasureIdx + 1} - pass ${currentPass}/${maxEndingNumber}, jumping back to measure ${repeatStartIdx + 1}`);
        return repeatStartIdx;
      } else {
        if (IS_DEVELOPMENT) console.log(`🎵 Playback: End Repeat at measure ${currentMeasureIdx + 1} - completed all ${maxEndingNumber} passes, continuing`);
      }
    }

    // Default: go to next measure
    return currentMeasureIdx + 1;
  }, [displayedChart, repeatPairMap]);

  // Simple ending detection
  const getEndingNumber = useCallback((measureIdx: number): number | null => {
    for (let i = measureIdx; i >= 0; i--) {
      const endingNum = displayedChart[i].annotations?.ending;
      if (endingNum !== undefined) {
        if (i === measureIdx) return endingNum;
        if (displayedChart[i].annotations?.startRepeat) {
          return null;
        }
        return endingNum;
      }
      if (displayedChart[i].annotations?.startRepeat && i < measureIdx) {
        return null;
      }
    }
    return null;
  }, [displayedChart]);

  // Check if we should skip a measure based on ending number and current pass
  const shouldSkipMeasure = useCallback((measureIdx: number, repeatPassCount: Map<number, number>): boolean => {
    const endingNumber = getEndingNumber(measureIdx);
    
    if (endingNumber === null) return false;
    
    let repeatStartIdx: number | undefined;
    let closestEndRepeat = Infinity;
    
    let endingStartMeasure = measureIdx;
    for (let i = measureIdx; i >= 0; i--) {
      if (displayedChart[i].annotations?.ending === endingNumber) {
        endingStartMeasure = i;
      } else if (displayedChart[i].annotations?.ending !== undefined && displayedChart[i].annotations?.ending !== endingNumber) {
        break;
      }
    }
    
    for (const [endIdx, startIdx] of Object.entries(repeatPairMap)) {
      const endRepeatIdx = parseInt(endIdx);
      if (endingStartMeasure >= startIdx && endingStartMeasure <= endRepeatIdx) {
        if (endRepeatIdx < closestEndRepeat) {
          closestEndRepeat = endRepeatIdx;
          repeatStartIdx = startIdx;
        }
      }
      else if (endingStartMeasure > endRepeatIdx && endRepeatIdx < closestEndRepeat) {
        let belongsToPrevious = true;
        for (let i = endRepeatIdx + 1; i < endingStartMeasure; i++) {
          if (displayedChart[i].annotations?.endRepeat) {
            belongsToPrevious = false;
            break;
          }
        }
        if (belongsToPrevious) {
          closestEndRepeat = endRepeatIdx;
          repeatStartIdx = startIdx;
        }
      }
    }
    
    if (repeatStartIdx === undefined) {
      if (IS_DEVELOPMENT) {
        console.log(`🎵 shouldSkipMeasure(${measureIdx + 1}): ending=${endingNumber}, NO repeat found`);
      }
      return false;
    }
    
    const currentPass = repeatPassCount.get(repeatStartIdx) || 1;
    
    if (IS_DEVELOPMENT) {
      console.log(`🎵 shouldSkipMeasure(${measureIdx + 1}): ending=${endingNumber}, repeatStart=${repeatStartIdx + 1}, currentPass=${currentPass}`);
    }
    
    const shouldPlay = endingNumber === currentPass;
    
    if (!shouldPlay && IS_DEVELOPMENT) {
      console.log(`🎵 Skipping measure ${measureIdx + 1} (ending ${endingNumber}, but we're on pass ${currentPass})`);
    }
    
    return !shouldPlay;
  }, [getEndingNumber, repeatPairMap, displayedChart]);

  // Pre-calculate special positions for navigation
  const navigationPositions = useMemo(() => {
    let segnoPosition: number | undefined;
    let codaPosition: number | undefined;
    let hasDalSegno = false;
    let hasDalCapo = false;
    let hasFine = false;
    let hasToCoda = false;

    displayedChart.forEach((measure, measureIndex) => {
      if (measure.annotations?.segno) segnoPosition = measureIndex;
      if (measure.annotations?.coda) codaPosition = measureIndex;
      if (measure.annotations?.dalSegno) hasDalSegno = true;
      if (measure.annotations?.dalCapo) hasDalCapo = true;
      if (measure.annotations?.fine) hasFine = true;
      if (measure.annotations?.toCoda) hasToCoda = true;
    });

    // Validation warnings (dev only)
    if (IS_DEVELOPMENT) {
      if (hasDalSegno && segnoPosition === undefined) {
        console.warn('⚠️ Chart has D.S. but no Segno marker — will jump to beginning');
      }
      if (hasToCoda && codaPosition === undefined) {
        console.warn('⚠️ Chart has To Coda but no Coda marker — jump will be ignored');
      }
      if ((hasDalCapo || hasDalSegno) && !hasFine && !hasToCoda) {
        console.warn('⚠️ Chart has D.C./D.S. but no Fine or To Coda — will play through twice then stop');
      }
    }

    return { segnoPosition, codaPosition, hasFine, hasToCoda };
  }, [displayedChart]);

  // Initialize AudioContext on user interaction
  useEffect(() => {
    const initAudio = () => {
      getAudioContext();
      window.removeEventListener('click', initAudio);
    };
    window.addEventListener('click', initAudio);
    return () => window.removeEventListener('click', initAudio);
  }, []);

  // Main Playback Loop
  useEffect(() => {
    if (!isPlaying) {
      clearAllTimers();
      return;
    }
  
    let lastActiveMeasureIndex = -1;
    for (let i = displayedChart.length - 1; i >= 0; i--) {
      if (displayedChart[i].chords.some(c => !!c)) {
        lastActiveMeasureIndex = i;
        break;
      }
    }
    
    if (IS_DEVELOPMENT) {
      console.log('🎵 Playback starting:', {
        lastActiveMeasureIndex: lastActiveMeasureIndex + 1,
        repeatPairMap,
        chartLength: displayedChart.length
      });
    }
  
    const measureDurationMs = (60 / audioSettings.bpm) * timeSignature.beats * 1000;
    
    repeatPassCountRef.current.clear();
    
    let hasSeenDalSegno = false;
    let hasSeenDalCapo = false;
    let isInRepeatPass = false;
  
    const playMeasure = (measureIdx: number) => {
      if (IS_DEVELOPMENT) {
        console.log(`🎵 playMeasure called with idx=${measureIdx + 1}, pass count:`, Object.fromEntries(repeatPassCountRef.current));
      }
      
      while (measureIdx < displayedChart.length && shouldSkipMeasure(measureIdx, repeatPassCountRef.current)) {
        measureIdx++;
      }
      
      if (IS_DEVELOPMENT) {
        console.log(`🎵 After skip check, playing measure ${measureIdx + 1}`);
      }
      
      if (measureIdx >= displayedChart.length || (lastActiveMeasureIndex !== -1 && measureIdx > lastActiveMeasureIndex)) {
        if (IS_DEVELOPMENT) {
          console.log(`🎵 Stopping: measureIdx=${measureIdx + 1}, lastActive=${lastActiveMeasureIndex + 1}, chartLength=${displayedChart.length}`);
        }
        setIsPlaying(false);
        setCurrentMeasure(null);
        setCurrentChordIndex(null);
        jumpedFromMeasuresRef.current.clear();
        repeatPassCountRef.current.clear();
        return;
      }
  
      setCurrentMeasure(measureIdx);
      
      const measure = displayedChart[measureIdx];
      const beatDuration = 60 / audioSettings.bpm;
      const chordDurationMs = measureDurationMs / measure.chords.length;
  
      chordTimersRef.current = measure.chords.map((_, chordIdx) =>
        Number(window.setTimeout(() => {
          setCurrentChordIndex(chordIdx);
        }, chordIdx * chordDurationMs))
      );

      if (audioSettings.metronomeEnabled && !isReadOnlyMode) {
        for (let i = 0; i < timeSignature.beats; i++) {
          playMetronomeClick({ time: getAudioContext().currentTime + i * beatDuration, beat: i + 1, timeSignature });
        }
      }
  
      measure.chords.forEach((chord, chordIdx) => {
        if (chord) {
          const parsedChord = parseChordToNotes(chord);
          if (parsedChord.notes) {
            playNotes(parsedChord, {
              ...audioSettings,
              duration: (chordDurationMs / 1000) * 0.9,
              startTime: getAudioContext().currentTime + chordIdx * (chordDurationMs / 1000),
            });
          }
        }
      });
  
      const playbackState = {
        jumpedFromMeasuresRef,
        repeatPassCount: repeatPassCountRef,
        hasSeenDalSegno,
        hasSeenDalCapo,
        isInRepeatPass,
        ...navigationPositions
      };
      
      const nextMeasureIdx = getNextMeasureIndex(measureIdx, playbackState);
      
      hasSeenDalSegno = playbackState.hasSeenDalSegno;
      hasSeenDalCapo = playbackState.hasSeenDalCapo;
      isInRepeatPass = playbackState.isInRepeatPass;
  
      mainTimerRef.current = Number(window.setTimeout(() => playMeasure(nextMeasureIdx), measureDurationMs));
    };
  
    playMeasure(currentMeasure ?? startMeasureIndex);
  
    return clearAllTimers;
  }, [isPlaying, displayedChart, audioSettings, timeSignature, startMeasureIndex, clearAllTimers, getNextMeasureIndex, shouldSkipMeasure, navigationPositions, mode]);

  // Handlers
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      clearAllTimers();
    } else {
      if (currentMeasure === null) {
        setCurrentMeasure(startMeasureIndex);
        setCurrentChordIndex(null);
        jumpedFromMeasuresRef.current.clear();
        repeatPassCountRef.current.clear();
      }
      setIsPlaying(true);
    }
  }, [isPlaying, currentMeasure, startMeasureIndex, clearAllTimers]);

  const handleRewind = useCallback(() => {
    setIsPlaying(false);
    setStartMeasureIndex(0);
    setCurrentMeasure(0);
    setCurrentChordIndex(null);
    jumpedFromMeasuresRef.current.clear();
    repeatPassCountRef.current.clear();
    clearAllTimers();
  }, [clearAllTimers]);

  const handlePlayChord = useCallback((chordString: string) => {
    if (!chordString) return;
    const parsedChord = parseChordToNotes(chordString);
    if (parsedChord.notes) {
      playNotes(parsedChord, { ...audioSettings, duration: 1.5, startTime: getAudioContext().currentTime });
    } else {
      console.warn(`Could not parse chord: ${chordString}`);
    }
  }, [audioSettings]);

  const handleSetStartMeasure = useCallback((index: number) => {
    setStartMeasureIndex(index);
    if (!isPlaying) {
      setCurrentMeasure(index);
      setCurrentChordIndex(null);
    }
  }, [isPlaying]);

  return {
    isPlaying,
    currentMeasure,
    currentChordIndex,
    startMeasureIndex,
    setStartMeasureIndex,
    setIsPlaying,
    setCurrentMeasure,
    setCurrentChordIndex,
    chartHasFine: navigationPositions.hasFine,
    chartHasToCoda: navigationPositions.hasToCoda,
    handlePlayPause,
    handleRewind,
    handleStop,
    handlePlayChord,
    handleSetStartMeasure,
    clearAllTimers,
  };
}
